import { Component, Inject, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { ViewMode } from "@coso/forms";
import { appendRowNodeIdToInputAriaLabel, TemplateRendererComponent } from "@coso/grid";
import { LoadingService } from "@coso/loading";
import { IS_SAAS } from "@coso/utility";
import { ColDef, GridOptions, RowNode, SelectionChangedEvent } from "ag-grid-community";
import { forkJoin } from "rxjs";
import { FilterType, FilterTypeName, HighAvailabilityModes, IConfigurationHolder, IExplicitFolderPermissionsDto, ISpecialPermissionHolder, PrincipalType } from "../../../../models/auto-generated";
import { AUserPermissionsService } from "../../../../services/a-user-permissions.service";
import { ExplicitFolderPermissionsService } from "../../../../services/explicit-folder-permissions.service";
import { ReplicationService } from "../../../../services/replication.service";
import { SettingsService } from "../../../../services/settings.service";
import { handleError } from "../../../../utils/handle-error";
import { isRunning } from "../../../../utils/is-running";
import { PrincipalTypeMap } from "../models/PrincipalTypeMap";
import { AddEditExplicitFolderPermissionComponent } from "./add-edit-explicit-folder-permission/add-edit-explicit-folder-permission.component";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";

@Component({
  standalone:false,
  templateUrl: "./explicit-folder-permissions.component.html",
  styleUrls: ["./explicit-folder-permissions.component.scss"]
})
export class ExplicitFolderPermissionsComponent implements OnInit {
  @ViewChild("actionButtons", { static: true }) actionButtons: TemplateRef<any>;

  successMessage: string;
  errorMessage: string;
  /** Indicates whether initialization failed. If true, page will be non-interactive. */
  initFailed: boolean;

  instanceId: string;
  sharePointId: number;
  spConfigIndex: number;
  rowData: ISpecialPermissionHolder[] = [];
  selectedRows: ISpecialPermissionHolder[] = [];
  isReadOnly: boolean;
  isRunning: boolean;
  isFailoverBackupInstance: boolean;
  /** Information about the explicit folder permissions */
  information: IExplicitFolderPermissionsDto;
  addEditModalRef: NgbModalRef;

  currentSettings: IConfigurationHolder;
  PrincipalType = PrincipalType;
  principalTypes = Object.keys(PrincipalType).filter((value) => isNaN(Number(value)));

  FilterType = FilterType;
  filterTypes = Object.keys(FilterType).filter((value) => isNaN(Number(value)));

  gridOptions: GridOptions;
  columnDefs: ColDef[];

  constructor(
    @Inject(IS_SAAS) public isSaaS: boolean,
    public loadingService: LoadingService,
    private route: ActivatedRoute,
    private modalService: NgbModal,
    private settingsService: SettingsService,
    private replicationService: ReplicationService,
    private userPermissionsService: AUserPermissionsService,
    private explicitFolderPermissionsService: ExplicitFolderPermissionsService
  ) {}

  ngOnInit(): void {
    this.instanceId = this.route.snapshot.params["instance-id"];
    this.sharePointId = this.route.snapshot.params["sharepoint-id"];
    this.gridOptions = {
      rowHeight: 40,
      rowSelection: "multiple",
      suppressRowClickSelection: true,
      isRowSelectable: this.isRowSelectable.bind(this),
      onSelectionChanged: this.onSelectionChanged.bind(this),
      onFirstDataRendered: (e) => {
        e.api.setGridAriaProperty("label", "List of Explicit Folder Permissions");
        e.api.forEachNode(appendRowNodeIdToInputAriaLabel);
      }
    };
    this.columnDefs = [
      {
        headerName: "Filter type",
        field: "filterType",
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        checkboxSelection: true,
        cellRenderer: (params) => FilterTypeName.get(params.value)
      },
      {
        headerName: "Filter value",
        field: "filterValue",
        cellRenderer: (params) => {
          if (params.data.FilterType !== FilterType.Entity) {
            return params.value ? params.value : "---";
          }
          // eslint-disable-next-line eqeqeq
          const entity = this.information.entities.find((element) => element.logicalName == params.value);
          return entity.displayName;
        }
      },
      {
        headerName: "Principal type",
        field: "principalType",
        cellRenderer: (params) => PrincipalTypeMap.get(params.value)
      },
      {
        headerName: "Principal value",
        valueGetter: (params) => {
          let principal: any;
          /* eslint-disable eqeqeq */
          if (params.data.principalType == PrincipalType.SharePointUser) {
            principal = this.information.principals.users.find((p) => p.id.toString() == params.data.principalValue);
          } else if (params.data.principalType == PrincipalType.SharePointGroup) {
            principal = this.information.principals.sharePointGroups.find((p) => p.id.toString() == params.data.principalValue);
          } else if (params.data.principalType == PrincipalType.DirectoryGroup) {
            principal = this.information.principals.directoryGroups.find((p) => p.id.toString() == params.data.principalValue);
          } else if (params.data.principalType == PrincipalType.CRMUser) {
            principal = this.information.users.find((p) => p.id.toString() == params.data.principalValue);
          } else if (params.data.principalType == PrincipalType.CRMTeam) {
            principal = this.information.teams.find((p) => p.id.toString() == params.data.principalValue);
          }
          /* eslint-enable eqeqeq */
          return principal ? principal.name : params.data.principalValue;
        },
        cellRenderer: (params) => params.value ?? "---"
      },
      { headerName: "SharePoint Permission Level", field: "privilegeLevelName" },
      {
        headerName: "Actions",
        width: 80,
        sortable: false,
        resizable: false,
        cellRenderer: TemplateRendererComponent,
        cellRendererParams: { ngTemplate: this.actionButtons }
      }
    ];
    this.loadSettings();
    this.loadInformation();
  }

  openCreateModal() {
    this.successMessage = undefined;
    this.addEditModalRef = this.modalService.open(AddEditExplicitFolderPermissionComponent, {
      ariaLabelledBy: "modalTitle",
      keyboard: true,
      backdrop: "static",
      modalDialogClass: "modal-lg"
    });
    this.addEditModalRef.componentInstance.viewMode = ViewMode.Create;
    this.addEditModalRef.componentInstance.groupStorage = this.currentSettings.groupStorage;
    this.addEditModalRef.componentInstance.information = this.information;
    this.addEditModalRef.componentInstance.saveCallback = this.upsertSpecialPermission.bind(this);
  }

  openEditModal(params: any) {
    this.successMessage = undefined;
    this.addEditModalRef = this.modalService.open(AddEditExplicitFolderPermissionComponent, {
      ariaLabelledBy: "modalTitle",
      keyboard: true,
      backdrop: "static",
      modalDialogClass: "modal-lg"
    });
    this.addEditModalRef.componentInstance.viewMode = this.isReadOnly ? ViewMode.View : ViewMode.Edit;
    this.addEditModalRef.componentInstance.entity = params.data;
    this.addEditModalRef.componentInstance.groupStorage = this.currentSettings.groupStorage;
    this.addEditModalRef.componentInstance.information = this.information;
    this.addEditModalRef.componentInstance.saveCallback = (holder: any) => {
      /** Removes the old explicit permission */
      const index = this.rowData.findIndex((e) => JSON.stringify(e) === JSON.stringify(params.data));
      this.rowData.splice(index, 1);
      this.upsertSpecialPermission(holder);
    };
  }

  removeSelected() {
    this.errorMessage = undefined;
    this.selectedRows.forEach((r) => {
      const index = this.rowData.findIndex((e) => JSON.stringify(e) === JSON.stringify(r));
      if (index > -1) this.rowData.splice(index, 1);
    });

    const newSettings = Object.assign({}, this.currentSettings);
    const newData = Object.assign([], this.rowData);
    newSettings.sharePoint.sharepointConfig[this.spConfigIndex].specialPermissions = newData;
    this.updateSettings(newSettings, null, (error) => (this.errorMessage = `Failed to remove explicit folder permission(s) due to: ${handleError(error)}`));
  }

  private isRowSelectable(_event: RowNode) {
    return this.isReadOnly === false;
  }

  private onSelectionChanged(event: SelectionChangedEvent) {
    event.api.forEachNode(appendRowNodeIdToInputAriaLabel);
    const rows = event.api.getSelectedRows();
    this.selectedRows = Object.assign([], rows);
  }

  private upsertSpecialPermission(newHolder: ISpecialPermissionHolder) {
    this.addEditModalRef.componentInstance.errorMessage = undefined;
    this.rowData.push(newHolder);
    const newSettings = Object.assign({}, this.currentSettings);
    const newRowData = Object.assign([], this.rowData);
    newSettings.sharePoint.sharepointConfig[this.spConfigIndex].specialPermissions = newRowData;
    this.updateSettings(
      newSettings,
      () => this.addEditModalRef.close(),
      (error) => (this.addEditModalRef.componentInstance.errorMessage = `Failed to save explicit folder permission due to: ${handleError(error)}`)
    );
  }

  private updateSettings(newValue: IConfigurationHolder, successCallback: () => void = null, errorCallback: (error: any) => void = null) {
    this.loadingService.start("Saving explicit folder permission", "saveSettings");
    this.settingsService.save(this.instanceId, newValue).subscribe(
      (data) => {
        this.selectedRows = [];
        this.rowData = data.sharePoint.sharepointConfig[this.spConfigIndex].specialPermissions;
        this.currentSettings = data;
        this.successMessage = `Explicit folder permission has been successfully saved.`;
        if (successCallback) successCallback();
        this.loadingService.stop("saveSettings");
      },
      (error) => {
        if (errorCallback) errorCallback(error);
        this.loadingService.stop("saveSettings");
      }
    );
  }

  private loadSettings() {
    forkJoin([this.replicationService.getStatus(this.instanceId), this.userPermissionsService.hasLimitedAccess$, this.settingsService.get(this.instanceId)]).subscribe(
      ([status, hasLimitedAccess, currentSettings]) => {
        this.isReadOnly = hasLimitedAccess;
        this.isRunning = isRunning(status);
        if (this.isSaaS === false) {
          this.isFailoverBackupInstance = currentSettings.highAvailabilityMode === HighAvailabilityModes.FailoverBackup;
          this.isReadOnly = this.isReadOnly || this.isFailoverBackupInstance || this.isRunning;
        }
        // eslint-disable-next-line eqeqeq
        this.spConfigIndex = currentSettings.sharePoint.sharepointConfig.findIndex((element) => element.key == this.sharePointId);
        this.rowData = currentSettings.sharePoint.sharepointConfig[this.spConfigIndex].specialPermissions ?? [];
        this.currentSettings = currentSettings;
      },
      (error) => {
        this.initFailed = true;
        this.errorMessage = `Something went wrong due to: ${handleError(error)}`;
      }
    );
  }

  private loadInformation() {
    this.loadingService.start("Loading explicit folder permissions information", "loadPermissions");
    this.explicitFolderPermissionsService.get(this.instanceId, this.sharePointId).subscribe(
      (data) => {
        this.information = data;
        this.loadingService.stop("loadPermissions");
      },
      (error) => {
        this.initFailed = true;
        this.errorMessage = `Something went wrong due to: ${handleError(error)}`;
        this.loadingService.stop("loadPermissions");
      }
    );
  }
}
